import React, { useState } from "react";
import { Send } from "lucide-react";
import { api } from "../../lib/api";
import {
  Section, TextField, TextAreaField, RadioField, CheckboxGroup,
  AcknowledgmentCheckbox, ACKNOWLEDGMENT_TEXT, Honeypot,
} from "./BookingFormFields";
import ScheduleField from "./ScheduleField";
import FileAttachmentField from "./FileAttachmentField";

const USE_TYPES = [
  "Rehearsal", "Workshop", "Class", "Reading", "Audition",
  "Community Gathering", "Youth Program", "Training", "Other",
];
const SPACES = ["Lobby", "Black Box"];
const YN = ["Yes", "No"];

const EMPTY = {
  full_name: "", organization: "", email: "", phone: "", website: "",
  use_type: "", project_name: "", purpose: "",
  requested_dates: "", recurring: "",
  start_time: "", end_time: "",
  participants: "",
  spaces: [],
  tech_support: "", equipment_brought: "", refreshments: "", food_description: "",
  community_serve: "", goals: "", budget: "",
  schedule: [],
  attachments: [],
  hp_url: "",
  acknowledged: false,
};

export default function CreativeCommunityForm({ onSuccess, onError }) {
  const [f, setF] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.acknowledged) return onError?.("Please confirm the acknowledgment to submit.");
    setSubmitting(true);
    try {
      const { full_name, email, phone, organization, hp_url, ...rest } = f;
      await api.post("/booking", {
        form_type: "creative",
        full_name, email, phone, organization,
        event_title: f.project_name || f.use_type || "Creative & Community Use",
        hp_url,
        data: rest,
      });
      setF(EMPTY);
      onSuccess?.();
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-12 relative" data-testid="creative-event-form">
      <Honeypot value={f.hp_url} onChange={set("hp_url")} testId="creative-hp-url" />
      <Section title="Contact Information">
        <TextField label="Full Name" value={f.full_name} onChange={set("full_name")} required testId="creative-full-name" autoComplete="name" />
        <TextField label="Organization (if applicable)" value={f.organization} onChange={set("organization")} testId="creative-org" autoComplete="organization" />
        <TextField label="Email Address" type="email" value={f.email} onChange={set("email")} required testId="creative-email" autoComplete="email" />
        <TextField label="Phone Number" type="tel" value={f.phone} onChange={set("phone")} required testId="creative-phone" autoComplete="tel" />
        <TextField label="Website / Social Media" value={f.website} onChange={set("website")} fullWidth testId="creative-website" />
      </Section>

      <Section title="Event Information">
        <RadioField label="Type of Use" value={f.use_type} onChange={set("use_type")} options={USE_TYPES} required testId="creative-use-type" />
        <TextField label="Project / Event Name" value={f.project_name} onChange={set("project_name")} required fullWidth testId="creative-project-name" />
        <TextAreaField label="Describe the purpose of your booking" value={f.purpose} onChange={set("purpose")} required rows={4} testId="creative-purpose" />
        <TextField label="Requested Date(s)" value={f.requested_dates} onChange={set("requested_dates")} required testId="creative-dates" />
        <RadioField label="Recurring Dates?" value={f.recurring} onChange={set("recurring")} options={YN} required testId="creative-recurring" />
        <TextField label="Start Time" value={f.start_time} onChange={set("start_time")} required testId="creative-start" />
        <TextField label="End Time" value={f.end_time} onChange={set("end_time")} required testId="creative-end" />
        <TextField label="Expected Number of Participants" value={f.participants} onChange={set("participants")} required fullWidth testId="creative-participants" type="number" min={1} max={100} placeholder="Max 100" />
        <CheckboxGroup label="Which space do you need?" values={f.spaces} onChange={set("spaces")} options={SPACES} testId="creative-spaces" />
        <ScheduleField
          label="Additional Dates / Multi-Session Schedule (optional)"
          hint="Use this if your booking spans multiple sessions (recurring class, weekend workshop, audition days, etc.). Skip if a single date is enough."
          value={f.schedule}
          onChange={set("schedule")}
          testId="creative-schedule"
        />
      </Section>

      <Section title="Event Support">
        <RadioField label="Will technical support be needed?" value={f.tech_support} onChange={set("tech_support")} options={YN} required testId="creative-tech" />
        <RadioField label="Will materials or equipment be brought in?" value={f.equipment_brought} onChange={set("equipment_brought")} options={YN} required testId="creative-equipment" />
        <RadioField label="Will food or refreshments be served?" value={f.refreshments} onChange={set("refreshments")} options={YN} required testId="creative-refreshments" />
        {f.refreshments === "Yes" && (
          <TextAreaField label="What type of food or refreshments?" value={f.food_description} onChange={set("food_description")} required testId="creative-food-description" placeholder="e.g. coffee & pastries, lunch boxes, etc." />
        )}
      </Section>

      <Section title="Final Questions">
        <TextAreaField label="How does this event serve your audience or community?" value={f.community_serve} onChange={set("community_serve")} required testId="creative-serve" />
        <TextAreaField label="What are your goals for this gathering?" value={f.goals} onChange={set("goals")} required testId="creative-goals" />
        <TextField label="Budget for space rental" value={f.budget} onChange={set("budget")} testId="creative-budget" />
        <FileAttachmentField
          label="Supporting Documents"
          hint="Optional. Curriculum, audition sides, run sheet, or any reference docs. PDF, Word, Excel, or images. Max 5 files, 10 MB each."
          value={f.attachments}
          onChange={set("attachments")}
          testId="creative-attachments"
        />
        <AcknowledgmentCheckbox checked={f.acknowledged} onChange={set("acknowledged")} testId="creative-acknowledged">
          {ACKNOWLEDGMENT_TEXT}
        </AcknowledgmentCheckbox>
      </Section>

      <div className="pt-6">
        <button type="submit" disabled={submitting} data-testid="creative-submit" className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3">
          {submitting ? "Sending…" : "Submit Inquiry"}
          {!submitting && <Send size={16} />}
        </button>
      </div>
    </form>
  );
}
